import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { publicOnboardService } from '../services/api';
import './SelfOnboard.css';

const SelfOnboard: React.FC = () => {
  const { hostelCode } = useParams<{ hostelCode: string }>();
  const [hostelName, setHostelName] = useState('');
  const [loadingHostel, setLoadingHostel] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [username, setUsername] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [address, setAddress] = useState('');
  const [workingPlace, setWorkingPlace] = useState('');
  const [emergencyContactName, setEmergencyContactName] = useState('');
  const [emergencyContactPhoneNumber, setEmergencyContactPhoneNumber] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [idFront, setIdFront] = useState<File | null>(null);
  const [idBack, setIdBack] = useState<File | null>(null);

  const joinUrl = useMemo(() => {
    if (!hostelCode) return '';
    return `${window.location.origin}/join/${hostelCode}`;
  }, [hostelCode]);

  useEffect(() => {
    if (!hostelCode) {
      setLoadingHostel(false);
      return;
    }
    publicOnboardService
      .getJoinHostel(hostelCode)
      .then((h) => setHostelName(h.name))
      .catch(() => toast.error('Invalid or inactive hostel link'))
      .finally(() => setLoadingHostel(false));
  }, [hostelCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hostelCode) return;
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append('username', username.trim());
      fd.append('phoneNumber', phoneNumber.trim());
      fd.append('password', password);
      fd.append('confirmPassword', confirmPassword);
      if (address.trim()) fd.append('address', address.trim());
      if (workingPlace.trim()) fd.append('workingPlace', workingPlace.trim());
      if (emergencyContactName.trim()) fd.append('emergencyContactName', emergencyContactName.trim());
      if (emergencyContactPhoneNumber.trim()) {
        fd.append('emergencyContactPhoneNumber', emergencyContactPhoneNumber.trim());
      }
      if (photo) fd.append('photo', photo);
      if (idFront) fd.append('idFrontSide', idFront);
      if (idBack) fd.append('idBackSide', idBack);

      const res = await publicOnboardService.submitApplication(hostelCode, fd);
      toast.success(res.message);
      setSubmitted(true);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Submission failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!hostelCode) {
    return (
      <div className="so-page">
        <p className="so-error">Invalid registration link.</p>
      </div>
    );
  }

  if (loadingHostel) {
    return (
      <div className="so-page">
        <p className="so-loading">Loading…</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="so-page">
        <div className="so-card so-card--success">
          <h1>Registration received</h1>
          <p>
            Thank you for joining <strong>{hostelName}</strong>! The hostel will review your details and
            assign your bed. You can sign in after approval.
          </p>
          <div className="so-success-links">
            <Link to="/login" className="so-success-link so-success-link--primary">
              Sign in
            </Link>
            <a href={joinUrl} className="so-success-link">
              Join link
            </a>
          </div>
          {joinUrl ? (
            <p className="so-success-url">
              <span>Share this link for others to join:</span>
              <a href={joinUrl} className="so-success-url__text">
                {joinUrl}
              </a>
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="so-page">
      <div className="so-card">
        <header className="so-header">
          <h1>Join {hostelName || 'hostel'}</h1>
          <p>Fill in your details. Photo and ID proofs are optional. Fields marked * are required.</p>
        </header>

        <form className="so-form" onSubmit={(e) => void handleSubmit(e)}>
          <div className="so-row">
            <label className="so-field">
              <span>Full name *</span>
              <input value={username} onChange={(e) => setUsername(e.target.value)} required />
            </label>
            <label className="so-field">
              <span>Phone *</span>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
              />
            </label>
          </div>

          <div className="so-row">
            <label className="so-field">
              <span>Password *</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={6}
                required
              />
            </label>
            <label className="so-field">
              <span>Confirm password *</span>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                minLength={6}
                required
              />
            </label>
          </div>

          <label className="so-field">
            <span>Address</span>
            <textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2} />
          </label>

          <div className="so-row">
            <label className="so-field">
              <span>Working place</span>
              <input value={workingPlace} onChange={(e) => setWorkingPlace(e.target.value)} />
            </label>
            <label className="so-field">
              <span>Emergency contact name</span>
              <input value={emergencyContactName} onChange={(e) => setEmergencyContactName(e.target.value)} />
            </label>
          </div>

          <label className="so-field">
            <span>Emergency contact phone</span>
            <input
              type="tel"
              value={emergencyContactPhoneNumber}
              onChange={(e) => setEmergencyContactPhoneNumber(e.target.value)}
            />
          </label>

          <div className="so-uploads">
            <label className="so-field">
              <span>Profile photo (optional)</span>
              <input type="file" accept="image/*,application/pdf,.pdf" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
            </label>
            <label className="so-field">
              <span>ID proof — front (optional)</span>
              <input type="file" accept="image/*,application/pdf,.pdf" onChange={(e) => setIdFront(e.target.files?.[0] || null)} />
            </label>
            <label className="so-field">
              <span>ID proof — back (optional)</span>
              <input type="file" accept="image/*,application/pdf,.pdf" onChange={(e) => setIdBack(e.target.files?.[0] || null)} />
            </label>
          </div>

          <button type="submit" className="so-submit" disabled={submitting || !hostelName}>
            {submitting ? 'Joining…' : 'Join'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SelfOnboard;
